import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Globe,
  ExternalLink,
  Check,
  X,
  HelpCircle,
  ShieldCheck,
  AlertTriangle,
  Sparkles,
  Flag,
  RefreshCw,
  Beaker,
  ChevronRight,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  getResearchQueue,
  recordResearcherVerdict,
  getResearchCategories,
  groundCitation,
  triangulateCitation,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { TranslateButton } from "../components/TranslateButton";

const CATEGORY_COLOR_MAP = {
  rose:    "border-rose-500/40 text-rose-300 bg-rose-500/5",
  amber:   "border-amber-500/40 text-amber-300 bg-amber-500/5",
  sky:     "border-sky-500/40 text-sky-300 bg-sky-500/5",
  emerald: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  violet:  "border-violet-500/40 text-violet-300 bg-violet-500/5",
  lime:    "border-lime-500/40 text-lime-300 bg-lime-500/5",
  cyan:    "border-cyan-500/40 text-cyan-300 bg-cyan-500/5",
  indigo:  "border-indigo-500/40 text-indigo-300 bg-indigo-500/5",
  yellow:  "border-yellow-500/40 text-yellow-300 bg-yellow-500/5",
  pink:    "border-pink-500/40 text-pink-300 bg-pink-500/5",
  fuchsia: "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/5",
  orange:  "border-orange-500/40 text-orange-300 bg-orange-500/5",
  purple:  "border-purple-500/40 text-purple-300 bg-purple-500/5",
  stone:   "border-stone-500/40 text-stone-300 bg-stone-500/5",
  slate:   "border-slate-500/40 text-slate-300 bg-slate-500/5",
  blue:    "border-blue-500/40 text-blue-300 bg-blue-500/5",
  teal:    "border-teal-500/40 text-teal-300 bg-teal-500/5",
  neutral: "border-neutral-500/40 text-neutral-300 bg-neutral-500/5",
  zinc:    "border-zinc-600 text-zinc-300 bg-zinc-500/5",
};

const STATUS_META = {
  fact:          { glyph: "✓",  label: "Fact",         color: "emerald", icon: ShieldCheck },
  web_cited:     { glyph: "🌐", label: "Web-cited",    color: "sky",     icon: Globe },
  contested:     { glyph: "⚠",  label: "Contested",    color: "amber",   icon: AlertTriangle },
  theory:        { glyph: "◆",  label: "Theory",       color: "violet",  icon: Sparkles },
  refuted:       { glyph: "✗",  label: "Refuted",      color: "rose",    icon: X },
  challenged:    { glyph: "?",  label: "Challenged",   color: "zinc",    icon: Flag },
  uncited:       { glyph: "⚠",  label: "Uncited",      color: "zinc",    icon: HelpCircle },
  single_source: { glyph: "◇",  label: "Single source", color: "zinc",    icon: Beaker },
};

const COLOR = {
  emerald: { border: "border-emerald-500/40", text: "text-emerald-300", bg: "bg-emerald-500/5", chipOn: "border-emerald-400 bg-emerald-500/15" },
  sky:     { border: "border-sky-500/40",     text: "text-sky-300",     bg: "bg-sky-500/5",     chipOn: "border-sky-400 bg-sky-500/15" },
  amber:   { border: "border-amber-500/40",   text: "text-amber-300",   bg: "bg-amber-500/5",   chipOn: "border-amber-400 bg-amber-500/15" },
  violet:  { border: "border-violet-500/40",  text: "text-violet-300",  bg: "bg-violet-500/5",  chipOn: "border-violet-400 bg-violet-500/15" },
  rose:    { border: "border-rose-500/40",    text: "text-rose-300",    bg: "bg-rose-500/5",    chipOn: "border-rose-400 bg-rose-500/15" },
  zinc:    { border: "border-zinc-700",       text: "text-zinc-300",    bg: "bg-zinc-500/5",    chipOn: "border-zinc-400 bg-zinc-500/10" },
};

const FILTER_ORDER = ["fact", "web_cited", "uncited", "single_source", "contested", "challenged", "theory", "refuted"];

function ResearcherVerdictBadge({ verdict }) {
  if (!verdict) return null;
  const map = {
    confirmed:           { color: "border-emerald-500/60 text-emerald-300 bg-emerald-500/10", icon: Check,  label: "Confirmed" },
    denied:              { color: "border-rose-500/60 text-rose-300 bg-rose-500/10",          icon: X,      label: "Denied" },
    needs_more_research: { color: "border-amber-500/60 text-amber-300 bg-amber-500/10",        icon: HelpCircle, label: "Needs more" },
  };
  const m = map[verdict];
  if (!m) return null;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.25em] border ${m.color}`}>
      <Icon className="h-3 w-3" />
      {m.label}
    </span>
  );
}

function CategoryBadge({ category, categories }) {
  const meta = (categories || []).find((c) => c.key === category) || {
    emoji: "◇", label: category || "uncategorized", color: "zinc"
  };
  const cls = CATEGORY_COLOR_MAP[meta.color] || CATEGORY_COLOR_MAP.zinc;
  return (
    <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border ${cls} inline-flex items-center gap-1`}>
      <span aria-hidden="true">{meta.emoji}</span>
      <span>{meta.label}</span>
    </span>
  );
}

function ResearchCard({ entry, categories, onMutate }) {
  const meta = STATUS_META[entry.epistemic_status] || STATUS_META.uncited;
  const cls = COLOR[meta.color];
  const Icon = meta.icon;
  const [grounding, setGrounding] = useState(false);
  const [triangulating, setTriangulating] = useState(false);
  const [recordingVerdict, setRecordingVerdict] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState(entry.researcher_note || "");

  const doGround = async () => {
    if (grounding) return;
    setGrounding(true);
    try {
      const r = await groundCitation(entry.citation_id);
      toast.success(`Grounded · ${(r?.grounding?.sources || []).length} sources`);
      onMutate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grounding failed");
    } finally {
      setGrounding(false);
    }
  };

  const doTriangulate = async () => {
    if (triangulating) return;
    setTriangulating(true);
    toast.info("Triangulating · Claude vs GPT vs Gemini arbiter…");
    try {
      const r = await triangulateCitation(entry.citation_id);
      const v = r?.verdict?.verdict || r?.epistemic_status || "verdict";
      toast.success(`Verdict: ${String(v).toUpperCase()}`);
      onMutate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Triangulation failed");
    } finally {
      setTriangulating(false);
    }
  };

  const doRecord = async (verdict) => {
    if (recordingVerdict) return;
    setRecordingVerdict(true);
    try {
      const res = await recordResearcherVerdict(entry.citation_id, verdict, note);
      // Surface the auto-seal mint feedback if the backend fired one —
      // architects see "Confirmed · sealed block #N · +X APT minted" so
      // the cause→effect of verification is obvious.
      const seal = res?.auto_seal;
      if (seal && seal.block_height) {
        toast.success(
          `Confirmed → sealed block #${seal.block_height} · ${seal.minted_count} coins minted (+${(seal.verifier_apt || 0).toFixed(2)} APT to verifier pool)`
        );
      } else {
        toast.success(`Marked: ${verdict.replace("_", " ")}`);
      }
      setShowNote(false);
      onMutate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setRecordingVerdict(false);
    }
  };

  return (
    <article
      data-testid={`research-card-${entry.citation_id}`}
      className={`border ${cls.border} ${cls.bg} bg-[#0b0b0d]`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`shrink-0 h-9 w-9 border ${cls.border} ${cls.text} flex items-center justify-center`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-[0.25em] border ${cls.border} ${cls.text}`}>
                {meta.glyph} {meta.label}
              </span>
              <CategoryBadge category={entry.category} categories={categories} />
              {entry.truth_block_height && (
                <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-[0.2em] border border-emerald-500/40 text-emerald-300 font-mono">
                  block #{entry.truth_block_height}
                </span>
              )}
              <ResearcherVerdictBadge verdict={entry.researcher_verdict} />
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
                {entry.retrieved_at ? new Date(entry.retrieved_at).toISOString().slice(0, 19).replace("T", " ") : ""}
              </span>
            </div>

            <p className="font-serif italic text-zinc-100 text-[14.5px] leading-snug">&ldquo;{entry.claim}&rdquo;</p>
            <TranslateButton
              text={entry.claim}
              inline
              testIdPrefix={`translate-claim-${entry.citation_id}`}
              className="mt-1.5"
            />

            {/* Atom-rewrite audit trail — when the architect rejected
                some atomic sub-claims, the citation stores ONLY the
                verified atoms. Show the submitter's original wording
                here so anyone auditing the chain can see what was
                stripped (and why). */}
            {entry.original_claim && entry.original_claim !== entry.claim && (
              <details
                className="mt-2 border border-zinc-800/60 bg-black/30"
                data-testid={`original-claim-${entry.citation_id}`}
              >
                <summary className="cursor-pointer select-none px-2.5 py-1.5 text-[9.5px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
                  ⌁ original wording · {entry.atoms_total || "?"} atoms · {entry.atoms_confirmed || 0} kept · {entry.atoms_rejected || 0} stripped
                </summary>
                <div className="px-2.5 pb-2.5 text-[12px] leading-snug text-zinc-400 italic">
                  &ldquo;{entry.original_claim}&rdquo;
                </div>
              </details>
            )}

            {/* Verification context — the original question + reply
                excerpt the claim was extracted from. Without this the
                verifier sees disembodied snippets like "It primarily
                happens in chloroplasts" with no idea what "it" was. */}
            {(entry.originating_user_message || entry.originating_assistant_reply) && (
              <details
                className="mt-3 border border-amber-500/20 bg-amber-500/[0.04] open:border-amber-500/40"
                data-testid={`origin-context-${entry.citation_id}`}
                open
              >
                <summary className="cursor-pointer select-none px-2.5 py-1.5 text-[10px] uppercase tracking-[0.3em] text-amber-400 flex items-center gap-1.5">
                  <MessageCircle className="h-3 w-3" /> verification context · click to collapse
                </summary>
                <div className="px-2.5 pb-2.5 space-y-2 text-[12.5px] leading-snug">
                  {entry.originating_user_message && (
                    <div>
                      <div className="text-[9.5px] uppercase tracking-[0.3em] text-zinc-500 mb-0.5 font-mono">
                        ▷ original question
                      </div>
                      <p className="text-zinc-200">{entry.originating_user_message}</p>
                      <TranslateButton
                        text={entry.originating_user_message}
                        inline
                        testIdPrefix={`translate-origin-${entry.citation_id}`}
                        className="mt-1.5"
                      />
                    </div>
                  )}
                  {entry.originating_assistant_reply && (
                    <div>
                      <div className="text-[9.5px] uppercase tracking-[0.3em] text-zinc-500 mb-0.5 font-mono">
                        ◁ assistant reply (excerpt the claim was extracted from)
                      </div>
                      <p className="text-zinc-300 whitespace-pre-wrap">
                        {entry.originating_assistant_reply}
                      </p>
                    </div>
                  )}
                </div>
              </details>
            )}
            {!entry.originating_user_message && !entry.originating_assistant_reply && (
              <div
                className="mt-2 text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-mono inline-flex items-center gap-1"
                data-testid={`origin-context-missing-${entry.citation_id}`}
                title="This citation predates v18.72 — its original chat context wasn't persisted."
              >
                <MessageCircle className="h-3 w-3" /> no original context · legacy citation
              </div>
            )}

            {/* Sources */}
            {(entry.grounding_sources || []).length > 0 && (
              <div className="mt-3 border border-sky-500/20 bg-sky-500/5 p-2.5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-sky-400 mb-1.5 flex items-center gap-1.5">
                  <Globe className="h-3 w-3" /> {entry.grounding_sources.length} web source{entry.grounding_sources.length === 1 ? "" : "s"} · open to research
                </div>
                <ul className="space-y-1">
                  {entry.grounding_sources.slice(0, 6).map((s, i) => (
                    <li key={i} className="text-[12px] leading-snug">
                      <a
                        href={s.uri}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1.5 text-sky-300 hover:text-sky-200 hover:underline"
                        data-testid={`research-source-${entry.citation_id}-${i}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        {s.title || (s.uri || "").replace(/^https?:\/\//, "").split("/")[0]}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Source ref fallback (archive/memory) */}
            {(!entry.grounding_sources || entry.grounding_sources.length === 0) && entry.source_excerpt && (
              <div className="mt-3 border border-zinc-800 p-2.5 bg-[#08090c]">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                  {entry.source_type} · {entry.source_ref || "(no live source)"}
                </div>
                <p className="text-[12px] text-zinc-400 italic leading-snug">&ldquo;{entry.source_excerpt}&rdquo;</p>
              </div>
            )}

            {/* Researcher note */}
            {entry.researcher_note && (
              <div className="mt-3 border-l-2 border-zinc-700 pl-3 text-[12.5px] text-zinc-300 italic">
                <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mr-1.5">your note:</span>
                {entry.researcher_note}
              </div>
            )}

            {/* Actions */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {!entry.has_grounding && (
                <button
                  onClick={doGround}
                  disabled={grounding}
                  data-testid={`research-ground-${entry.citation_id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10.5px] uppercase tracking-[0.2em] border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 disabled:opacity-40"
                  title="Ground via Gemini Google Search"
                >
                  <Globe className="h-3 w-3" />
                  {grounding ? "Grounding…" : "Ground"}
                </button>
              )}
              {entry.triangulation_count === 0 && (
                <button
                  onClick={doTriangulate}
                  disabled={triangulating}
                  data-testid={`research-triangulate-${entry.citation_id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10.5px] uppercase tracking-[0.2em] border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40"
                  title="Cross-verify via Claude + GPT + Gemini arbiter"
                >
                  <ShieldCheck className="h-3 w-3" />
                  {triangulating ? "Triangulating…" : "Triangulate"}
                </button>
              )}
              <div className="ml-auto flex items-center gap-1.5">
                {!showNote ? (
                  <>
                    <button
                      onClick={() => doRecord("confirmed")}
                      disabled={recordingVerdict}
                      data-testid={`research-confirm-${entry.citation_id}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10.5px] uppercase tracking-[0.2em] border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40"
                    >
                      <Check className="h-3 w-3" />
                      Confirm
                    </button>
                    <button
                      onClick={() => doRecord("denied")}
                      disabled={recordingVerdict}
                      data-testid={`research-deny-${entry.citation_id}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10.5px] uppercase tracking-[0.2em] border border-rose-500/60 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40"
                    >
                      <X className="h-3 w-3" />
                      Deny
                    </button>
                    <button
                      onClick={() => setShowNote(true)}
                      data-testid={`research-note-${entry.citation_id}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10.5px] uppercase tracking-[0.2em] border border-zinc-700 text-zinc-300 hover:border-zinc-500"
                    >
                      <HelpCircle className="h-3 w-3" />
                      Note…
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setShowNote(false)}
                    className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-300"
                  >
                    cancel
                  </button>
                )}
              </div>
            </div>

            {showNote && (
              <div className="mt-3 border border-zinc-800 p-3 bg-[#08090c]">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="Note your finding before marking…"
                  className="w-full bg-transparent text-sm text-zinc-100 outline-none resize-none"
                  data-testid={`research-note-input-${entry.citation_id}`}
                />
                <div className="mt-2 flex items-center gap-1.5">
                  <button
                    onClick={() => doRecord("confirmed")}
                    disabled={recordingVerdict}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10.5px] uppercase tracking-[0.2em] border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10"
                  >
                    <Check className="h-3 w-3" /> Confirm with note
                  </button>
                  <button
                    onClick={() => doRecord("denied")}
                    disabled={recordingVerdict}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10.5px] uppercase tracking-[0.2em] border border-rose-500/60 text-rose-300 hover:bg-rose-500/10"
                  >
                    <X className="h-3 w-3" /> Deny with note
                  </button>
                  <button
                    onClick={() => doRecord("needs_more_research")}
                    disabled={recordingVerdict}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10.5px] uppercase tracking-[0.2em] border border-amber-500/60 text-amber-300 hover:bg-amber-500/10"
                  >
                    <HelpCircle className="h-3 w-3" /> Needs more
                  </button>
                </div>
              </div>
            )}
          </div>
          <ChevronRight className="hidden sm:block shrink-0 h-4 w-4 text-zinc-700" />
        </div>
      </div>
    </article>
  );
}

export default function Research() {
  const { user } = useAuth();
  const isArchitect = !!user?.is_architect;
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // v19.40.4 · client-side search over claim + source excerpt + category.
  // Debounced so a user typing "boil" doesn't refire the filter memo on
  // every keystroke. 180 ms feels instant without churning.
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim().toLowerCase()), 180);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getResearchQueue({ status: filter || undefined, category: categoryFilter || undefined, limit: 200, includeResolved: true }),
      getResearchCategories().catch(() => null),
    ])
      .then(([d, c]) => {
        if (cancelled) return;
        setData(d);
        if (c) setCategories(c);
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter, categoryFilter, refreshTick]);

  // Background auto-grounding fires ~10s after a chat reply. Poll
  // every 12s while the page is open so new sources appear without
  // a manual refresh.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(() => setRefreshTick((x) => x + 1), 12_000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const totals = data?.totals || {};
  const rawEntries = data?.entries || [];

  // v19.40.4 · client-side search across the *currently loaded* page
  // of results. Backend delivers up to 200 rows; the search filters
  // them on the fly. If the user wants deeper coverage they can pick
  // a category chip first, which re-queries the API server-side.
  const entries = useMemo(() => {
    if (!searchQuery) return rawEntries;
    return rawEntries.filter((e) => {
      const hay = [
        e.claim,
        e.category,
        e.source_excerpt,
        e.epistemic_status,
        e.source_ref,
        (e.grounding_sources || [])
          .map((s) => `${s?.title || ""} ${s?.uri || ""}`)
          .join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(searchQuery);
    });
  }, [rawEntries, searchQuery]);

  const headline = useMemo(() => {
    const parts = [];
    if (filter) parts.push((STATUS_META[filter] || {}).label || filter);
    if (categoryFilter) {
      const m = (categories?.categories || []).find((c) => c.key === categoryFilter);
      parts.push(`${m?.emoji || "◇"} ${m?.label || categoryFilter}`);
    }
    if (searchQuery) parts.push(`"${searchQuery}"`);
    if (parts.length === 0) return `All citations · ${entries.length}`;
    return `${parts.join(" · ")} · ${entries.length}`;
  }, [filter, categoryFilter, categories, searchQuery, entries.length]);

  return (
    <div className="min-h-[calc(100vh-130px)] bg-[#09090b] text-zinc-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-sky-400 mb-1 inline-flex items-center gap-2">
              <Search className="h-3 w-3" /> △ public fact database · searchable
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-[0.15em] text-zinc-100">
              Research &amp; Verify
            </h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
              Every citation the Sentinel has anchored — with live web
              sources — indexed by <span className="text-sky-300">category</span>{" "}
              and <span className="text-emerald-300">epistemic status</span>.
              Type a topic in the search bar to see whether the network
              already holds a verdict on it, or use the chips below to
              browse the whole database by category.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                data-testid="auto-refresh-toggle"
              />
              auto-refresh
            </label>
            <button
              onClick={() => setRefreshTick((t) => t + 1)}
              data-testid="research-refresh-btn"
              className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] border border-zinc-800 text-zinc-400 hover:border-emerald-500/60 hover:text-emerald-300"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Search bar · v19.40.4 · client-side fuzzy over the loaded page */}
        <div className="mb-4">
          <label className="relative block">
            <span className="sr-only">Search facts</span>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search claims, categories, source titles… (e.g. water boiling, NASA, election)"
              data-testid="research-search-input"
              className="w-full bg-[#0b0b0d] border border-zinc-800 focus:border-sky-500/60 focus:outline-none pl-10 pr-9 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 font-body transition-colors"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                data-testid="research-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-rose-300"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </label>
          {searchQuery && (
            <div
              className="mt-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono"
              data-testid="research-search-summary"
            >
              {entries.length} match{entries.length === 1 ? "" : "es"} for &ldquo;{searchQuery}&rdquo;
              {rawEntries.length !== entries.length && (
                <span className="text-zinc-600"> · of {rawEntries.length} loaded</span>
              )}
            </div>
          )}
        </div>

        {/* Status filter strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
          {FILTER_ORDER.map((s) => {
            const m = STATUS_META[s];
            const cls = COLOR[m.color];
            const active = filter === s;
            const count = totals[s] ?? 0;
            return (
              <button
                key={s}
                onClick={() => setFilter(active ? null : s)}
                data-testid={`research-filter-${s}`}
                className={`text-left p-2.5 border transition-colors ${active ? cls.chipOn : cls.border} ${cls.bg} hover:bg-zinc-900/40`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-[9.5px] uppercase tracking-[0.2em] ${cls.text}`}>
                    {m.glyph} {m.label}
                  </span>
                  <span className="font-mono text-base tabular-nums text-zinc-100">{count}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Category filter strip — horizontally scrollable */}
        {categories?.categories && (
          <div className="mb-6" data-testid="category-filter-strip">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <Search className="h-3 w-3" /> categories · scroll →
            </div>
            <div className="relative">
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-zinc-800 [&::-webkit-scrollbar-track]:bg-transparent">
                <button
                  onClick={() => setCategoryFilter(null)}
                  data-testid="cat-filter-all"
                  className={`shrink-0 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                    !categoryFilter
                      ? "border-zinc-400 bg-zinc-700/30 text-zinc-100"
                      : "border-zinc-800 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  All · <span className="font-mono">{categories.total}</span>
                </button>
                {categories.categories.filter((c) => c.count > 0 || c.key === categoryFilter).map((c) => {
                  const active = categoryFilter === c.key;
                  const cls = CATEGORY_COLOR_MAP[c.color] || CATEGORY_COLOR_MAP.zinc;
                  return (
                    <button
                      key={c.key}
                      onClick={() => setCategoryFilter(active ? null : c.key)}
                      data-testid={`cat-filter-${c.key}`}
                      className={`shrink-0 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${cls} ${active ? "ring-2 ring-offset-2 ring-offset-[#09090b] ring-current" : "hover:bg-zinc-900/40"}`}
                    >
                      <span className="mr-1.5">{c.emoji}</span>
                      {c.label} · <span className="font-mono">{c.count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          <span>{headline}</span>
          {(filter || categoryFilter) && (
            <button
              onClick={() => { setFilter(null); setCategoryFilter(null); }}
              data-testid="research-clear-filter"
              className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-200 border border-zinc-800 hover:border-zinc-600 px-2 py-1"
            >
              clear filters
            </button>
          )}
        </div>

        {/* Body */}
        {loading && !data && (
          <div className="text-zinc-500 text-sm py-12 text-center font-mono uppercase tracking-[0.25em]" data-testid="research-loading">
            loading citations…
          </div>
        )}
        {error && (
          <div className="text-rose-400 text-sm py-6 border border-rose-500/40 bg-rose-500/5 px-4 font-mono" data-testid="research-error">
            {String(error)}
          </div>
        )}
        {!loading && !error && entries.length === 0 && (
          <div className="text-zinc-500 text-sm py-16 text-center border border-zinc-800 bg-[#0b0b0d]" data-testid="research-empty">
            <div className="font-heading text-lg text-zinc-300 mb-2">No citations yet</div>
            <p className="text-xs uppercase tracking-[0.25em]">Ask the Sentinel a factual question — its citations land here within seconds.</p>
          </div>
        )}
        {entries.length > 0 && (
          <div className="space-y-3" data-testid="research-list">
            {entries.map((e) => (
              <ResearchCard
                key={e.citation_id}
                entry={e}
                categories={categories?.categories || []}
                onMutate={() => setRefreshTick((t) => t + 1)}
              />
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-zinc-900 text-[10px] uppercase tracking-[0.3em] text-zinc-600 leading-loose">
          <div>{data?.notes?.purpose || "Personal research queue."}</div>
          <div className="mt-1">
            Auto-grounded claims feed the Truth Compiler · denied claims are purged · confirmed claims earn full anchor weight in future chats.
          </div>
        </div>
      </div>
    </div>
  );
}
